import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  IInternationalStudentContactForm,
  IStrapiDocument,
  MakeAClaimPayload,
} from 'src/app/interfaces/strapi';
import { ApiService } from 'src/app/services/api.service';
import StrapiService from 'src/app/services/strapi.service';
import { HttpClient } from '@angular/common/http';
import { User } from 'src/app/shared/models/user.model';
import { NgIf, NgFor } from '@angular/common';
import { BreadcrumbsComponent } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { AuthService } from '../../../services/auth.service';
import { TooltipComponent } from '../../../shared/components/tooltip/tooltip.component';
import { ModalComponent } from '../../../shared/components/modal/modal.component';
import { TooltipModalComponent } from '../../../shared/components/tooltip-modal/tooltip-modal.component';

@Component({
    selector: 'app-make-a-claim-form',
    templateUrl: './make-a-claim-form.component.html',
    styleUrls: ['./make-a-claim-form.component.scss'],
    imports: [
        BreadcrumbsComponent,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        TooltipComponent,
        NgFor,
        ModalComponent,
        TooltipModalComponent,
    ],
})
export class MakeAClaimFormComponent implements OnInit {
  public contactFormData: IInternationalStudentContactForm = <
    IInternationalStudentContactForm
  >{};

  public claimForm!: UntypedFormGroup;

  public showTooltipModel = false;

  public tooltipModalContent: string = '';

  public submitted = false;

  public showSuccessMessage = false;

  public contactFormError = false;

  public svgHtml: any;

  private cdr = inject(ChangeDetectorRef);

  constructor(
    private strapi: StrapiService,
    public readonly formBuilder: UntypedFormBuilder,
    public readonly auth: AuthService,
    public api: ApiService,
    public http: HttpClient
  ) {}

  ngOnInit(): void {
    this.strapi.getInternationalStudentContactForm().subscribe({
      next: (
        contactForm: IStrapiDocument<IInternationalStudentContactForm>
      ) => {
        this.contactFormData = contactForm.data.attributes;
        this.cdr.markForCheck();
      },
    });
    this.auth.populateUserSubject.subscribe((user: User) => {
      this.initialiseForm(user);
    });
  }

  public initialiseForm(user: User): void {
    const studentName = `${user.firstName ?? ''} ${user.lastName ?? ''} `;
    this.claimForm = this.formBuilder.group({
      student_name: [studentName, [Validators.required.bind(this)]],
      contact_number: ['', [Validators.pattern('^[0-9]*$')]],
      email: [user.email, [Validators.required.bind(this)]],
      policy_number: [
        user.university.pathway_provider_policy_number,
        [Validators.required.bind(this)],
      ],
      centre_name: [user.university.name, Validators.required.bind(this)],
      language: ['English', Validators.required.bind(this)],
      claim_reason: ['', Validators.required.bind(this)],
      description: ['', Validators.required.bind(this)],
    });
    this.cdr.markForCheck();
  }

  public submit(): void {
    this.submitted = true;

    if (this.formControl['contact_number'].errors?.['pattern']) return;

    const payload = {
      title: this.contactFormData.email_title,
      intro: this.contactFormData.email_intro,
      name: String(this.claimForm.get('student_name')!.value),
      email: String(this.claimForm.get('email')!.value),
      contact: String(this.claimForm.get('contact_number')!.value),
      centre_name: String(this.claimForm.get('centre_name')!.value),
      policy_number: String(this.claimForm.get('policy_number')!.value),
      language: String(this.claimForm.get('language')!.value),
      reason_for_claim: String(this.claimForm.get('claim_reason')!.value),
      description: String(this.claimForm.get('description')!.value),
    } as MakeAClaimPayload;

    this.api.internationalStudentMakeAClaim(payload).subscribe({
      next: () => {
        this.showSuccessMessage = true;
        this.contactFormError = false;
      },
      error: () => {
        this.contactFormError = true;
      },
    });
  }

  public get formControl(): {
    [key: string]: AbstractControl;
  } {
    return this.claimForm.controls;
  }

  public openTooltip(id: string) {
    this.showTooltipModel = true;

    type PageDataIndex = keyof typeof this.contactFormData;

    const currentRoute = id as PageDataIndex;
    this.tooltipModalContent = this.contactFormData[currentRoute] as string;
  }
}
